import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Gift, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useNoIndex } from "@/lib/useNoIndex";
import { privacyNotice } from "@/lib/privacy";
import { t, nameForm } from "@/lib/i18n";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";

export default function AnswerPage() {
  useNoIndex();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [coll, setColl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [respondentEmail, setRespondentEmail] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLangState] = useState(searchParams.get("lang") === "de" ? "de" : "fi");
  const setLang = (l) => {
    setLangState(l);
    const p = new URLSearchParams(searchParams);
    p.set("lang", l);
    setSearchParams(p, { replace: true });
  };
  const draftKey = `portwrite-draft-${slug}`;

  useEffect(() => {
    api
      .get(`/collections/${slug}`)
      .then(({ data }) => {
        setColl(data);
        if (data.subject_type === "community") {
          try {
            const saved = JSON.parse(localStorage.getItem(`portwrite-draft-${slug}`) || "null");
            if (saved) {
              setAnswers(saved.answers || {});
              setName(saved.name || "");
              setRespondentEmail(saved.respondentEmail || "");
              setAnonymous(!!saved.anonymous);
              if (saved.lang) setLang(saved.lang);
              toast.success(t("answer.draftRestored", {}, saved.lang || "fi"));
            }
          } catch { /* ignore */ }
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const saveDraft = () => {
    localStorage.setItem(draftKey, JSON.stringify({ answers, name, respondentEmail, anonymous, lang }));
    toast.success(t("answer.draftSaved", {}, lang));
  };

  const langQuestions = coll ? ((coll.questions_by_lang && coll.questions_by_lang[lang]) || coll.questions) : [];

  const submit = async (e) => {
    e.preventDefault();
    const hasAny = (langQuestions || []).some((q) => (answers[q.key] || "").trim());
    if (!hasAny) {
      toast.error(t("answer.err.atLeastOne", {}, lang));
      return;
    }
    if (!anonymous && !name.trim()) {
      toast.error(t("answer.err.nameOrAnon", {}, lang));
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/collections/${slug}/answers`, {
        respondent_name: anonymous ? null : name,
        is_anonymous: anonymous,
        respondent_email: respondentEmail.trim() || null,
        answers,
        language: lang,
      });
      localStorage.removeItem(draftKey);
      navigate(`/v/${slug}/kiitos?lang=${lang}`);
    } catch (err) {
      toast.error(t("answer.err.submitFailed", {}, lang));
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-[#7A6B63]">{t("common.loading")}</div>;
  }

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center px-4 text-center">
        <div>
          <h1 className="font-serif text-3xl text-[#2C221E]">{t("answer.notFound.title")}</h1>
          <p className="mt-2 text-[#7A6B63]">{t("answer.notFound.body")}</p>
        </div>
      </div>
    );
  }

  const { person_name, occasion_title, closed, closes_at, subject_type, languages } = coll;
  const isCommunity = subject_type === "community";
  const questions = langQuestions;
  const availableLangs = (languages || ["fi"]).filter((l) => l === "fi" || l === "de");
  const showLangSwitch = availableLangs.length > 1;

  const closesLabel = closes_at
    ? new Date(closes_at).toLocaleDateString(lang === "de" ? "de-DE" : "fi-FI", { timeZone: "Europe/Helsinki", day: "2-digit", month: "2-digit", year: "numeric" })
    : null;

  if (closed) {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md text-center rounded-3xl border border-[#E8DEC2] bg-white p-8 shadow-sm"
          data-testid="collection-closed-message"
        >
          <Lock className="w-8 h-8 text-[#C86D51] mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-[#2C221E]">{t("answer.closed.title", {}, lang)}</h1>
          <p className="mt-3 text-[#7A6B63]">{t("answer.closed.body", {}, lang)}</p>
        </motion.div>
      </div>
    );
  }

  const langSwitch = showLangSwitch ? (
    <div className="mt-5 inline-flex rounded-full border border-[#E8DEC2] bg-white p-1" data-testid="answer-language-switch">
      {availableLangs.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => { setLang(l); setStep(0); }}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${lang === l ? "bg-[#C86D51] text-white" : "text-[#7A6B63] hover:text-[#B35A3E]"}`}
          data-testid={`answer-lang-${l}`}
        >
          {l === "de" ? t("answer.langDe") : t("answer.langFi")}
        </button>
      ))}
    </div>
  ) : null;

  const fiAllative = lang === "fi" ? nameForm(coll.name_forms, "fi", "allative") : null;
  const headingKey = isCommunity
    ? "answer.headingCommunity"
    : (lang === "fi" && !fiAllative ? "answer.headingPerson.fb" : "answer.headingPerson");
  const headingName = fiAllative || person_name;
  const introKey = lang === "fi" && !fiAllative ? "answer.intro.fb" : "answer.intro";
  const introName = fiAllative || person_name;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-14">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-2 text-[#C86D51] mb-4">
            <Gift className="w-6 h-6" />
            <span className="text-sm font-semibold tracking-wide uppercase">Portwrite</span>
          </div>
          {langSwitch}
          <h1 className="mt-4 font-serif text-3xl sm:text-4xl font-medium text-[#2C221E] leading-tight">            {t(headingKey, { name: headingName }, lang)}
          </h1>
          <div className="mt-5 rounded-2xl bg-[#F7F2EC] border border-[#E8DEC2] p-5 text-[#4A3E39] leading-relaxed" data-testid="answer-intro">
            {t(introKey, { name: introName, occasion: occasion_title }, lang)}
          </div>
          {closesLabel && (
            <p className="mt-3 text-sm text-[#7A6B63]" data-testid="answer-deadline">
              {t("answer.deadline", { date: closesLabel }, lang)}
            </p>
          )}
        </motion.div>

        <div className="mt-6 rounded-2xl border border-[#E9C9BC] bg-[#FBECE7] p-4 text-sm text-[#8a4b38] leading-relaxed" data-testid="answer-privacy-notice">
          {privacyNotice(lang)}{" "}
          <Link to="/tietosuoja" className="underline font-medium" data-testid="answer-privacy-link">
            {t("answer.privacyLink", {}, lang)}
          </Link>
        </div>

        <div className="mt-4 rounded-2xl bg-[#F7F2EC] border border-[#E8DEC2] p-4 text-[#4A3E39]" data-testid="answer-book-notice">
          <p className="text-sm leading-relaxed">
            {t("answer.bookNotice", {}, lang)}
          </p>
          <p className="mt-2 text-sm font-semibold text-[#B35A3E]" data-testid="answer-live-counter">
            {t("answer.liveCounter", { n: coll.answer_count }, lang)}
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="answer-form">
          {isCommunity && step < questions.length && (
            <div className="text-[#B35A3E] font-semibold text-lg" data-testid="progress-indicator">
              {t("answer.progress", { n: step + 1, total: questions.length }, lang)}
            </div>
          )}
          {(isCommunity ? questions.slice(step, step + 1) : questions).map((q, idx) => {
            const i = isCommunity ? step : idx;
            return (
              <motion.div
                key={q.key}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: isCommunity ? 0 : 0.05 * i }}
                className="rounded-2xl border border-[#E8DEC2] bg-white p-5 sm:p-6 shadow-sm"
              >
                <Label htmlFor={q.key} className={`block ${isCommunity ? "text-xl" : "text-base"} text-[#2C221E] leading-relaxed mb-3`}>
                  {q.text}
                </Label>
                <Textarea
                  id={q.key}
                  value={answers[q.key] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.key]: e.target.value }))}
                  placeholder={t("answer.placeholder", {}, lang)}
                  className={`rounded-xl border-[#E8DEC2] bg-[#FDFBF7] leading-relaxed resize-y ${isCommunity ? "min-h-[240px] text-[18px]" : "min-h-[120px] text-base"}`}
                  data-testid={`answer-textarea-${i + 1}`}
                />
              </motion.div>
            );
          })}

          {isCommunity && (
            <div className="flex flex-wrap items-center gap-3">
              {step > 0 && (
                <Button type="button" variant="outline" onClick={() => setStep(step - 1)}
                  className="h-12 text-base rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]" data-testid="prev-question-button">
                  {t("answer.prev", {}, lang)}
                </Button>
              )}
              {step < questions.length && (
                <Button type="button" onClick={() => setStep(step + 1)}
                  className="h-12 text-base bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl" data-testid="next-question-button">
                  {t("answer.next", {}, lang)}
                </Button>
              )}
              <Button type="button" variant="ghost" onClick={saveDraft}
                className="h-12 text-base text-[#7A6B63] hover:text-[#B35A3E]" data-testid="save-draft-button">
                {t("answer.saveDraft", {}, lang)}
              </Button>
            </div>
          )}

          {(!isCommunity || step === questions.length) && (
          <div className="rounded-2xl border border-[#E8DEC2] bg-white p-5 sm:p-6 shadow-sm space-y-4">
            {!anonymous && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#2C221E] text-base">{t("answer.nameLabel", {}, lang)}</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("answer.namePlaceholder", {}, lang)}
                  className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-base"
                  data-testid="respondent-name-input"
                />
              </div>
            )}
            <div className="flex items-center gap-3">
              <Checkbox
                id="anon"
                checked={anonymous}
                onCheckedChange={(v) => setAnonymous(!!v)}
                data-testid="anonymous-checkbox"
              />
              <Label htmlFor="anon" className="text-[#4A3E39] text-base cursor-pointer">
                {t("answer.anonLabel", {}, lang)}
              </Label>
            </div>
            <div className="space-y-2 pt-2 border-t border-[#F0E8DA]">
              <Label htmlFor="respondent-email" className="text-[#2C221E] text-base">{t("answer.emailLabel", {}, lang)}</Label>
              <Input
                id="respondent-email"
                type="email"
                value={respondentEmail}
                onChange={(e) => setRespondentEmail(e.target.value)}
                placeholder={t("common.emailPlaceholder", {}, lang)}
                className="h-12 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-base"
                data-testid="respondent-email-input"
              />
              <p className="text-sm text-[#7A6B63]">{t("answer.emailHelp", {}, lang)}</p>
            </div>
          </div>
          )}

          {(!isCommunity || step === questions.length) && (
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-base bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl"
            data-testid="submit-answer-button"
          >
            {submitting ? t("answer.submitting", {}, lang) : t("answer.submit", {}, lang)}
          </Button>
          )}
        </form>
      </div>
      <Footer slug={slug} />
    </div>
  );
}
